import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";

export function PolicyGate() {
  const { user, logout } = useAuth();
  const qc = useQueryClient();
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const { data } = useQuery({
    queryKey: ["legal-pending", user?.id],
    queryFn: async () => (await api.get("/legal/pending")).data,
    enabled: !!user && user.role !== "admin",
    refetchOnWindowFocus: true,
  });
  const pending = data?.pending || [];
  if (!pending.length) return null;

  const accept = async () => {
    setBusy(true);
    try {
      await api.post("/legal/accept", { kinds: pending.map((p) => p.kind) });
      qc.setQueryData(["legal-pending", user.id], { pending: [] });
      setAgreed(false);
      toast.success("Thank you — your acceptance has been recorded");
    } catch (e) {
      toast.error(errMsg(e));
      setAgreed(false);
      qc.invalidateQueries({ queryKey: ["legal-pending", user.id] });
    } finally {
      setBusy(false);
    }
  };

  const names = pending.map((p) => p.title).join(" and ");
  return (
    <Dialog open>
      <DialogContent className="max-h-[90vh] w-[calc(100%-1.5rem)] overflow-y-auto rounded-3xl bg-[#FDFBF7] sm:rounded-3xl [&>button]:hidden" data-testid="policy-update-dialog"
        onEscapeKeyDown={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <p className="font-mono-ig text-[11px] uppercase tracking-[0.3em] text-[#9C6218]">Policy update</p>
          <DialogTitle className="font-serif text-3xl leading-tight">We've updated our {names}</DialogTitle>
          <DialogDescription className="text-[#6E5549]">Please review the changes and accept to keep using Ink Genius.</DialogDescription>
        </DialogHeader>
        <ul className="space-y-3">
          {pending.map((p) => (
            <li key={p.kind} className="rounded-2xl border border-[#E6DBCF] bg-white/70 p-4" data-testid={`policy-update-${p.kind}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="font-semibold text-[#2C1810]">{p.title}</p>
                {p.effective_date && <p className="text-xs text-[#6E5549]">Effective {p.effective_date}</p>}
              </div>
              {p.change_summary && <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-[#4A3B32]" data-testid={`policy-summary-${p.kind}`}>{p.change_summary}</p>}
              <Link to={`/${p.kind}`} target="_blank" data-testid={`policy-read-${p.kind}`} className="mt-3 inline-block text-xs font-semibold uppercase tracking-[0.18em] text-[#9C6218] underline-offset-4 hover:underline">
                Read the full {p.title} →
              </Link>
            </li>
          ))}
        </ul>
        <label className="flex cursor-pointer items-start gap-3 rounded-xl bg-[#FDF2F0] p-4">
          <Checkbox data-testid="policy-agree-checkbox" checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} className="mt-0.5 h-5 w-5 border-[#9C6218] data-[state=checked]:bg-[#2C1810]" />
          <span className="text-sm leading-relaxed text-[#2C1810]">I have read and agree to the updated {names}.</span>
        </label>
        <div className="flex flex-col gap-3 sm:flex-row">
          <button onClick={accept} disabled={!agreed || busy} data-testid="policy-accept-btn"
            className="min-h-[48px] flex-1 rounded-full bg-[#2C1810] px-6 text-sm font-semibold text-[#FDFBF7] hover:bg-[#3D2317] disabled:cursor-not-allowed disabled:opacity-50">
            {busy ? "Saving…" : "Accept and continue"}
          </button>
          <button onClick={logout} disabled={busy} data-testid="policy-signout-btn"
            className="min-h-[48px] rounded-full border border-[#E6DBCF] px-6 text-sm font-semibold text-[#2C1810] hover:bg-[#F6F1EA]">
            Sign out
          </button>
        </div>
        <p className="text-xs text-[#6E5549]">If you don't agree, you can sign out, or delete your account from your Profile page after accepting. Questions: info@moorebeautylove.com</p>
      </DialogContent>
    </Dialog>
  );
}
